<template>
  <dx-modal :value="show" :title="title" size="xl" :closable="!saving"
            @input="close()" id="modal-billing-guide" class="gd-scope">
    <div class="loading loading-lg gd-loading" v-if="!ready"></div>
    <template v-else>
      <div class="gd-readonly-banner" :class="bannerClass" v-if="isReadonly">
        <guide-status-badge :status="status" :batch-number="batchNumber" size="sm"/>
        <div class="msg">
          <b>{{ readonlyTitle }}</b>
          {{ readonlyText }}
        </div>
        <div class="actions">
          <button class="btn btn-primary btn-icon btn-icon-left"
                  :class="{loading: printing === 'guide'}" :disabled="!!printing"
                  @click="print()">
            <fa-icon :icon="['fal', 'print']"/>Imprimir
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left"
                  v-if="currentType === 'sadt'"
                  :class="{loading: printing === 'request'}" :disabled="!!printing"
                  @click="print(true)">
            <fa-icon :icon="['fal', 'file-alt']"/>Guia de solicitação
          </button>
          <router-link class="btn btn-gray btn-icon btn-icon-left"
                       v-if="account && account.batch && canListBatches"
                       :to="`/financial/billing-batches/${account.batch.id}`">
            <fa-icon :icon="['fal', 'layer-group']"/>Ver lote
          </router-link>
        </div>
      </div>

      <guide-context-bar
        :patient="patientName"
        :insurance="insuranceName"
        :professional="executant ? executant.name : ''"
        :professional-detail="executantCouncil"
        :start-date="contextDate"
        :status="account ? status : null"
        :batch-number="batchNumber"
        :referral-number="account ? account.referral.provider : null"
      />

      <div class="stenci-group stenci-alert gd-pending-alert"
           v-if="!isReadonly && pending.length > 0">
        <b>{{ pending.length }} {{ pending.length === 1 ? 'pendência' : 'pendências' }}:</b>
        {{ ' ' }}
        <template v-for="(issue, index) in pending">
          <a :key="issue.code" @click="focusField(issue.field)">{{ issue.message }}</a>
          <template v-if="index < pending.length - 1"> · </template>
        </template>
      </div>

      <div class="gd-reprice-alert" v-if="showDivergence">
        <fa-icon :icon="['fal', 'exclamation-triangle']"/>
        <div class="msg">
          <b>Preço congelado diferente da regra vigente</b>
          {{ divergenceText }}
        </div>
        <button class="btn btn-sm btn-gray btn-icon btn-icon-left"
                :class="{loading: reprice.loading}" :disabled="reprice.loading || saving"
                @click="openReprice">
          <fa-icon :icon="['fal', 'sync']"/>Atualizar preços e repasse
        </button>
      </div>

      <div class="gd-grid">
        <div class="gd-main">
          <guide-header-fields
            :form="form" :v="$v.form" :type="currentType" :plan-settings="planSettings"
            :readonly="isReadonly" :is-new="isNew"
            @insurance-referral="insuranceReferralChanged"
            @password="passwordChanged"
          />
          <guide-requester-fields
            v-if="showRequester"
            :form="form" :v="$v.form" :required="currentType === 'sadt'"
            :professionals="professionalPeople" :executant="executant" :readonly="isReadonly"
          />
          <guide-authorization-fields
            v-if="currentType === 'sadt'"
            :form="form" :v="$v.form" :type="currentType" :plan-settings="planSettings"
            :readonly="isReadonly"
            @authorization-date="fillAuthorizationValidity"
            @password="passwordChanged"
          />
          <div ref="items" v-if="isConsultation">
            <guide-consultation-item
              :items="form.items"
              :v="itemsValidation"
              :professionals="professionalOptions"
              :readonly="isReadonly"
              :pricing-keys="pricingKeys"
              :hide-insurance-value="hideInsuranceValue"
              @change="repriceItems([$event])"
              @select="selectConsultation"
            />
          </div>
          <guide-service-fields
            v-if="currentType !== 'simple'"
            :form="form" :v="$v.form" :type="currentType"
            :section="isConsultation ? '§4' : '§5'"
            :has-consultation-item="hasConsultationItem" :readonly="isReadonly"
            @consultation-type="consultationTypeChanged"
          />
          <div ref="items" v-if="!isConsultation">
            <guide-items
              :items="form.items"
              :v="itemsValidation"
              :professionals="professionalOptions"
              :reducers="reducers"
              :status="status"
              :selectable="!isReadonly"
              :readonly="isReadonly"
              :allow-add="canAddItems"
              :adding="addModal.loading"
              :removing="removingKey"
              :pricing-keys="pricingKeys"
              :hide-insurance-value="hideInsuranceValue"
              :single-consultation="currentType === 'sadt'"
              @change="repriceItems([$event])"
              @factor="factorEdited"
              @toggle="toggleItem"
              @remove="removeItem"
              @add="openAddItems"
            />
          </div>
          <div class="gd-section">
            <div class="gd-section-head">
              <span class="n">{{ justificationSection }}</span>
              <span class="t">Observação / justificativa</span>
              <span class="d" v-if="justificationHint">{{ justificationHint }}</span>
            </div>
            <div class="gd-section-body">
              <div class="form-group" v-if="!isReadonly">
                <textarea class="form-input" rows="2" v-model="form.service.justification"
                          placeholder="Opcional"></textarea>
              </div>
              <guide-read-field label="Observação" :value="form.service.justification" v-else/>
            </div>
          </div>
        </div>
        <div class="gd-side">
          <guide-totals
            :totals="totals"
            :single="isConsultation"
            :courtesy="form.courtesy"
            :loading="pricingKeys.length > 0"
            :readonly="isReadonly"
            :informed="account && status !== 'open' ? account.totals.informed : null"
            :hide-insurance-value="hideInsuranceValue"
            @update:courtesy="setCourtesy"
          />
          <guide-checklist
            v-if="currentType !== 'simple' || !checklistResult.ready"
            :checks="checklistResult.checks"
            :ready="checklistResult.ready"
            :readonly="isReadonly"
            @focus="focusField"
          />
        </div>
      </div>
    </template>

    <template slot="footer">
      <template v-if="!isReadonly">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1"
                :class="{loading: saving}" :disabled="!canSave" @click="save">
          <fa-icon :icon="['fal', 'check']"/>Salvar guia
        </button>
        <button class="btn btn-gray btn-icon btn-icon-left mr-1" v-if="account"
                :class="{loading: printing === 'guide'}" :disabled="!!printing || loading"
                @click="print()">
          <fa-icon :icon="['fal', 'print']"/>Imprimir
        </button>
        <button class="btn btn-gray btn-icon btn-icon-left mr-1"
                v-if="account && currentType === 'sadt'"
                :class="{loading: printing === 'request'}" :disabled="!!printing || loading"
                @click="print(true)">
          <fa-icon :icon="['fal', 'file-alt']"/>Guia de solicitação
        </button>
        <button class="btn btn-gray btn-icon btn-icon-left mr-1" v-if="canReprice"
                :class="{loading: reprice.loading}"
                :disabled="reprice.loading || saving || loading" @click="openReprice">
          <fa-icon :icon="['fal', 'sync']"/>Atualizar preços e repasse
        </button>
      </template>
      <button class="btn float-right" @click="close()">Sair</button>
    </template>

    <dx-modal :value="addModal.show" title="Adicionar itens pendentes" size="md"
              @input="addModal.show = false" id="modal-billing-guide-add" v-if="addModal.show">
      <div class="loading loading-lg" v-if="addModal.loading"></div>
      <template v-else>
        <div class="empty" v-if="addModal.items.length === 0">
          <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="2x"/></div>
          <p class="empty-title h6">Nenhum item pendente</p>
          <p class="empty-subtitle">
            Todos os serviços deste agendamento com este convênio já estão em guia ou pagos.
          </p>
        </div>
        <table class="table table-hover" v-else>
          <thead>
          <tr>
            <th style="width: 30px"></th>
            <th>Serviço</th>
            <th class="text-center">Qtde</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="expense in addModal.items" :key="expense.id" class="c-hand"
              @click="toggleAddItem(expense)">
            <td>
              <label class="form-checkbox">
                <input type="checkbox" :checked="addModal.selected.includes(expense.id)">
                <i class="form-icon"></i>
              </label>
            </td>
            <td>
              <span class="text-bold">{{ expense.expense.name }}</span>
              <span class="gd-sub">{{ expense.expense.code | tuss }}</span>
            </td>
            <td class="text-center">{{ expense.quantity || 1 }}</td>
          </tr>
          </tbody>
        </table>
      </template>
      <template slot="footer">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1"
                :disabled="addModal.selected.length === 0" @click="confirmAddItems">
          <fa-icon :icon="['fal', 'plus']"/>Adicionar
        </button>
        <button class="btn" @click="addModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <guide-reprice-modal
      v-if="reprice.show"
      :show="reprice.show"
      :preview="reprice.preview"
      :applying="reprice.applying"
      :guide-number="guideNumber"
      @confirm="applyReprice"
      @close="reprice.show = false"
    />
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import {
  required, requiredIf, minLength, maxLength, minValue, maxValue,
} from 'vuelidate/lib/validators';
import {
  date, hour, minDate, maxDate,
} from '@/data/validators';
import { UPDATE_ACCOUNT, LIST_BILLINGS } from '@/data/actions/modules/financial';
import { getLongName } from '@/data/billing-guide-types';
import { customName } from '@/helpers/insurance';
import currency from '@/filters/currency';
import checklist from '../checklist';
import {
  create, update, get, removeItem, resolvePrices, loadPlan, loadAppointment,
  repricePreview, repriceApply, printRoute, printRequestRoute, idOf, kindOf, reducersOf,
  toDate, toTime,
} from '../service';
import GuideStatusBadge from '../components/GuideStatusBadge.vue';
import GuideContextBar from '../components/GuideContextBar.vue';
import GuideHeaderFields from '../components/GuideHeaderFields.vue';
import GuideRequesterFields from '../components/GuideRequesterFields.vue';
import GuideAuthorizationFields from '../components/GuideAuthorizationFields.vue';
import GuideServiceFields from '../components/GuideServiceFields.vue';
import GuideItems from '../components/GuideItems.vue';
import GuideConsultationItem from '../components/GuideConsultationItem.vue';
import GuideTotals from '../components/GuideTotals.vue';
import GuideChecklist from '../components/GuideChecklist.vue';
import GuideReadField from '../components/GuideReadField.vue';
import GuideRepriceModal from './GuideRepriceModal.vue';

const AUTO_REFERRAL = 'AUTO';

function blankForm() {
  return {
    startDate: '',
    startTime: '',
    referral: { provider: '', insurance: '', main: '' },
    insured: { record: '', name: '', validity: '' },
    patient: { cns: '', socialName: '', isNewborn: false },
    request: {
      professionalId: null,
      professional: {
        name: '',
        council: { name: '', state: '', record: '' },
        specialty: { code: '', name: '' },
      },
      date: '',
    },
    authorization: { date: '', password: '', validity: '' },
    service: {
      type: '',
      character: '',
      consultationType: '',
      accidentIndication: '',
      serviceRegime: '',
      specialCoverage: '',
      occupationalHealth: '',
      closureReason: '',
      clinicalIndication: '',
      justification: '',
    },
    courtesy: false,
    professionalId: null,
    specialtyCode: null,
    items: [],
  };
}

function blankItem() {
  return {
    key: null,
    id: null,
    selected: true,
    appointmentExpenseId: null,
    splitIndex: 0,
    expenseId: null,
    expense: {
      id: null, code: '', name: '', type: '', tableCode: null,
    },
    kind: 'procedure',
    professionalId: null,
    specialtyCode: null,
    professionalName: null,
    accreditedName: null,
    quantity: 1,
    factor: 1,
    factorEdited: false,
    reduction: null,
    return: false,
    way: '',
    technique: '',
    degreeParticipation: '',
    executionDate: '',
    startTime: '',
    endTime: '',
    sequencialItem: null,
    unit: { insurance: 0, particular: 0 },
    value: { insurance: 0, particular: 0, total: 0 },
    priced: false,
    source: 'none',
    pricingError: false,
    rules: null,
  };
}

function orEmpty(value) {
  return value === null || value === undefined ? '' : value;
}

function nullable(value) {
  return value === '' || value === undefined ? null : value;
}

export default {
  components: {
    GuideStatusBadge,
    GuideContextBar,
    GuideHeaderFields,
    GuideRequesterFields,
    GuideAuthorizationFields,
    GuideServiceFields,
    GuideItems,
    GuideConsultationItem,
    GuideTotals,
    GuideChecklist,
    GuideReadField,
    GuideRepriceModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'sadt',
    },
    accountId: {
      type: String,
      default: null,
    },
    appointment: {
      type: Object,
      default: null,
    },
    insurance: {
      type: Object,
      default: null,
    },
    preselectedExpenseIds: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: 'billing',
    },
  },
  data() {
    return {
      loading: true,
      ready: false,
      saving: false,
      printing: null,
      removingKey: null,
      account: null,
      plan: null,
      planSettings: {},
      professionals: [],
      appointmentData: null,
      form: blankForm(),
      snapshot: '',
      pricingKeys: [],
      divergence: null,
      reprice: {
        show: false,
        loading: false,
        applying: false,
        preview: null,
      },
      addModal: {
        show: false,
        loading: false,
        items: [],
        selected: [],
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    isNew() {
      return !this.account;
    },
    currentType() {
      return this.account ? this.account.type : this.type;
    },
    isConsultation() {
      return this.currentType === 'consultation';
    },
    justificationSection() {
      if (this.isConsultation) return '§5';
      return this.currentType === 'simple' ? '§3' : '§7';
    },
    justificationHint() {
      if (this.isConsultation) return 'Campo 23';
      return this.currentType === 'simple' ? '' : 'Campo 58';
    },
    status() {
      return this.account ? this.account.status : 'open';
    },
    batchNumber() {
      return this.account && this.account.batch ? this.account.batch.number : null;
    },
    canUpdate() {
      return this.$can(UPDATE_ACCOUNT);
    },
    canListBatches() {
      return this.$can(LIST_BILLINGS);
    },
    isReadonly() {
      return this.readonly || this.status !== 'open' || !this.canUpdate;
    },
    canReprice() {
      return !!this.account && !this.isReadonly && !this.account.batch
        && !this.hideInsuranceValue;
    },
    showDivergence() {
      return this.canReprice && !!this.divergence && this.divergence.changed > 0;
    },
    divergenceText() {
      if (!this.divergence) return '';
      const { changed, before, after } = this.divergence;
      const items = `${changed} ${changed === 1 ? 'item muda' : 'itens mudam'}`;
      const insurance = `convênio ${currency(before.insurance)} → ${currency(after.insurance)}`;
      const transfer = `repasse ${currency(before.transfer)} → ${currency(after.transfer)}`;
      return `${items} · ${insurance} · ${transfer}`;
    },
    guideNumber() {
      return this.account ? this.account.referral.provider : null;
    },
    bannerClass() {
      return this.status === 'closed' ? 'closed' : '';
    },
    readonlyTitle() {
      if (this.status === 'in_batch') return `Guia no lote nº ${this.batchNumber || ''}`;
      if (this.status === 'closed') return `Guia encerrada com o lote nº ${this.batchNumber || ''}`;
      return 'Sem permissão para editar guias';
    },
    readonlyText() {
      if (this.status === 'in_batch') {
        return 'Somente leitura. Para alterar, retire a guia do lote '
          + '(só enquanto o lote está fechado e nada foi pago).';
      }
      if (this.status === 'closed') return 'O lote foi baixado; a guia não muda mais.';
      return 'Você pode consultar e imprimir esta guia.';
    },
    tissEnabled() {
      return !!(this.planSettings.tiss && this.planSettings.tiss.enabled);
    },
    generalSettings() {
      return this.planSettings.general || {};
    },
    recordSettings() {
      return this.generalSettings.record || {};
    },
    providerMode() {
      const settings = this.generalSettings.providerReferral;
      return settings && settings.mode ? settings.mode : 'manual';
    },
    sadtSettings() {
      return this.planSettings.tiss && this.planSettings.tiss.sadt
        ? this.planSettings.tiss.sadt
        : {};
    },
    authorizationSettings() {
      return this.sadtSettings.authorization || {};
    },
    consultationSettings() {
      return this.planSettings.tiss && this.planSettings.tiss.consultation
        ? this.planSettings.tiss.consultation
        : {};
    },
    reducers() {
      return reducersOf(this.planSettings);
    },
    billingSettings() {
      return this.generalSettings.billing || {};
    },
    hideInsuranceValue() {
      return this.source === 'appointment' && !!this.billingSettings.hideInsuranceValue;
    },
    isReturnGuide() {
      const items = this.selectedItems;
      return this.form.service.consultationType === 'return'
        || (items.length > 0 && items.every(item => item.return));
    },
    insuranceName() {
      if (this.plan) {
        return customName({ name: this.plan.insurance.name, plan: { name: this.plan.name } });
      }
      if (this.account) {
        return customName({
          name: this.account.insurance.name,
          plan: { name: this.account.insurance.plan.name },
        });
      }
      return this.insurance ? this.insurance.name || '' : '';
    },
    patientName() {
      if (this.account) return this.account.patient.name;
      return this.appointment && this.appointment.patient ? this.appointment.patient.name : '';
    },
    contextDate() {
      if (!this.form.startDate) return null;
      return moment(`${this.form.startDate} ${this.form.startTime || '00:00'}`).toDate();
    },
    title() {
      const parts = [getLongName(this.currentType)];
      if (this.insuranceName) parts.push(this.insuranceName);
      if (this.account && this.account.referral.provider) {
        parts.push(`nº ${this.account.referral.provider}`);
      }
      return parts.join(' · ');
    },
    professionalOptions() {
      return this.professionals.map(item => ({
        value: `${item.id}|${item.specialty ? item.specialty.code : ''}`,
        label: item.specialty && item.specialty.name
          ? `${item.name} (${item.specialty.name})`
          : item.name,
        id: item.id,
        name: item.name,
        specialty: item.specialty,
        council: item.council,
      }));
    },
    professionalPeople() {
      return this.professionals;
    },
    executant() {
      if (!this.form.professionalId) return null;
      const found = this.professionals.find(item => item.id === this.form.professionalId
        && (!this.form.specialtyCode || !item.specialty
          || item.specialty.code === this.form.specialtyCode))
        || this.professionals.find(item => item.id === this.form.professionalId);
      if (found) return found;
      if (this.account && this.account.professional) {
        return {
          id: this.account.professional.id,
          name: this.account.professional.name,
          council: this.account.professional.council,
          specialty: this.account.professional.specialty,
        };
      }
      return null;
    },
    executantText() {
      const professional = this.executant;
      if (!professional) return '';
      return this.executantCouncil
        ? `${professional.name} · ${this.executantCouncil}`
        : professional.name;
    },
    executantCouncil() {
      const council = this.executant ? this.executant.council : null;
      return council && council.record ? `${council.name}-${council.state} ${council.record}` : '';
    },
    hasRequester() {
      return !!this.form.request.professional.name;
    },
    showRequester() {
      return this.currentType === 'sadt';
    },
    selectedItems() {
      return this.form.items.filter(item => item.selected);
    },
    selectedCount() {
      return this.selectedItems.length;
    },
    selectedConsultationCount() {
      return this.selectedItems.filter(item => item.expense.type === 'consultation').length;
    },
    hasConsultationItem() {
      return this.selectedItems.some(item => item.expense.type === 'consultation');
    },
    totals() {
      const selected = this.selectedItems;
      const sum = key => selected
        .reduce((total, item) => total + (Number(item.value[key]) || 0), 0);
      return {
        insurance: Math.round(sum('insurance') * 100) / 100,
        particular: Math.round(sum('particular') * 100) / 100,
        count: this.form.items.length,
        selected: selected.length,
        procedures: selected.filter(item => item.kind === 'procedure').length,
        others: selected.filter(item => item.kind === 'other_expense').length,
      };
    },
    virtualAccount() {
      return {
        type: this.currentType,
        startDate: this.form.startDate,
        referral: {
          ...this.form.referral,
          provider: this.form.referral.provider
            || (this.providerMode === 'automatic' && this.isNew ? AUTO_REFERRAL : ''),
        },
        insurance: { insured: this.form.insured },
        request: this.form.request,
        authorization: this.form.authorization,
        service: this.form.service,
        items: this.selectedItems.map(item => ({
          expense: item.expense,
          kind: item.kind,
          return: item.return,
          way: item.way,
          technique: item.technique,
          rules: item.rules,
          price: { priced: item.priced },
          professional: {
            name: item.professionalName,
            specialty: { code: item.specialtyCode },
            degreeParticipation: item.degreeParticipation || null,
          },
        })),
      };
    },
    checklistResult() {
      if (this.isReadonly && this.status !== 'open') {
        return {
          checks: [{
            code: 'done', ok: true, message: 'Tudo certo · a guia entrou no lote', level: null,
          }],
          issues: [],
          ready: true,
        };
      }
      return checklist(this.virtualAccount, this.planSettings);
    },
    pending() {
      return this.checklistResult.issues.filter(issue => issue.level === 'block');
    },
    itemsValidation() {
      return {
        $error: this.$v.selectedCount.$error || this.$v.selectedConsultationCount.$error,
        singleConsultation: this.$v.selectedConsultationCount.$error,
        items: this.$v.form.items,
      };
    },
    canSave() {
      return !this.saving && !this.loading && this.pricingKeys.length === 0 && !this.isReadonly;
    },
    canAddItems() {
      return !this.isConsultation && !this.isReadonly
        && !!(this.appointment || this.appointmentId);
    },
    appointmentId() {
      if (this.appointment && this.appointment.id) return this.appointment.id;
      if (this.account && this.account.items.length > 0) {
        return this.account.items[0].appointmentId;
      }
      return null;
    },
    planId() {
      if (this.plan) return this.plan.id;
      if (this.account) return this.account.insurance.plan.id;
      return this.insurance ? this.insurance.planId : null;
    },
    insuranceId() {
      if (this.plan) return this.plan.insurance.id;
      if (this.account) return this.account.insurance.id;
      return this.insurance ? this.insurance.id : null;
    },
    requiresAuthorizationByItem() {
      return this.selectedItems.some(item => item.rules && item.rules.requireAuthorization);
    },
    requiresClinicalIndicationByItem() {
      return this.selectedItems.some(item => item.rules && item.rules.requireClinicalIndication);
    },
    dirty() {
      return this.snapshot !== JSON.stringify(this.form);
    },
  },
  validations() {
    const type = this.currentType;
    const isSadt = type === 'sadt';
    const isConsultation = type === 'consultation';
    const record = this.recordSettings;
    const auth = this.authorizationSettings;
    const validStart = this.form.startDate && moment(this.form.startDate, 'YYYY-MM-DD', true).isValid();
    const startDate = validStart ? this.form.startDate : null;
    const providerRequired = !(this.providerMode === 'automatic' && this.isNew);
    const insuranceReferralRequired = this.providerMode === 'insurance_referral'
      || (isConsultation && !!this.consultationSettings.requiredInsuranceReferral)
      || (isSadt && (!!auth.requiredInsuranceReferral || this.requiresAuthorizationByItem));
    const passwordRequired = isSadt
      && (!!auth.requiredPassword || this.requiresAuthorizationByItem);
    const requesterRequired = isSadt;

    const rules = {
      selectedCount: { minValue: minValue(1) },
      selectedConsultationCount: isSadt ? { maxValue: maxValue(1) } : {},
      form: {
        startDate: { required, date },
        startTime: { required, hour },
        referral: {
          provider: providerRequired ? { required } : {},
          insurance: insuranceReferralRequired ? { required } : {},
          main: isSadt && auth.requiredMainReferral ? { required } : {},
        },
        insured: {
          record: {},
          validity: { date },
          name: {},
        },
        request: isConsultation
          ? {}
          : {
            date: { date },
            professional: {
              name: requesterRequired ? { required } : {},
              council: {
                name: requesterRequired ? { required } : {},
                state: requesterRequired ? { required } : {},
                record: requesterRequired ? { required } : {},
              },
              specialty: {
                code: requesterRequired ? { required } : {},
              },
            },
          },
        authorization: isConsultation
          ? { password: {} }
          : {
            date: { date },
            password: passwordRequired ? { required } : {},
            validity: { date },
          },
        service: {
          type: isSadt ? { required } : {},
          character: isSadt ? { required } : {},
          consultationType: isConsultation || (isSadt && this.hasConsultationItem)
            ? { required }
            : {},
          accidentIndication: type !== 'simple' ? { required } : {},
          serviceRegime: type !== 'simple' ? { required } : {},
          clinicalIndication: !isConsultation && this.requiresClinicalIndicationByItem
            ? { required }
            : {},
        },
        items: {
          $each: {
            professionalId: {
              required: requiredIf(item => item.selected),
            },
            quantity: { minValue: minValue(1) },
            way: {
              required: requiredIf(item => !isConsultation && item.selected
                && !!(item.rules && item.rules.requireWayTechnique)),
            },
            technique: {
              required: requiredIf(item => !isConsultation && item.selected
                && !!(item.rules && item.rules.requireWayTechnique)),
            },
          },
        },
      },
    };

    const digitsEnabled = !!(record.digits && record.digits.enabled);
    if (!this.isReturnGuide) {
      if (record.required || digitsEnabled) {
        rules.form.insured.record = { required };
        rules.form.insured.name = { required };
      }
      if (digitsEnabled) {
        rules.form.insured.record = {
          ...rules.form.insured.record,
          minLength: minLength(record.digits.min || 1),
          maxLength: maxLength(record.digits.max || 20),
        };
      }
      if (record.requiredValidity) {
        rules.form.insured.validity = { required, date };
      }
    }

    if (isSadt) {
      if (this.sadtSettings.requiredRequestDate) {
        rules.form.request.date = { required, date };
      }
      if (this.form.request.date && startDate) {
        rules.form.request.date = { ...rules.form.request.date, maxDate: maxDate(startDate) };
      }
      if (auth.requiredDate || this.requiresAuthorizationByItem) {
        rules.form.authorization.date = { required, date };
      }
      if (auth.requiredValidity || this.requiresAuthorizationByItem) {
        rules.form.authorization.validity = { required, date };
      }
    }

    if (!isConsultation && this.form.authorization.validity && this.form.authorization.date) {
      rules.form.authorization.validity = {
        ...rules.form.authorization.validity,
        minDate: minDate(this.form.authorization.date),
      };
    }

    return rules;
  },
  async mounted() {
    document.addEventListener('keydown', this.onKeydown);
    await this.init();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    ...mapActions({
      apiFetchProfessionals: 'apiFetchProfessionals',
    }),
    async init() {
      this.loading = true;
      try {
        await this.loadProfessionals();
        if (this.accountId) {
          await this.loadAccount();
        } else {
          await this.buildNew();
        }
        this.snapshot = JSON.stringify(this.form);
      } catch (e) {
        this.$toast.error(e);
        this.$emit('close', false);
        return;
      } finally {
        this.loading = false;
      }
      if (this.isNew && !this.isReadonly) {
        await this.repriceItems(this.form.items);
        this.snapshot = JSON.stringify(this.form);
      }
      this.ready = true;
      this.loadDivergence();
    },
    async loadDivergence() {
      if (!this.canReprice) return;
      try {
        this.divergence = await repricePreview(this.account.id);
      } catch (e) {
        this.divergence = null;
      }
    },
    async openReprice() {
      if (!this.canReprice) return;
      if (this.dirty) {
        this.$toast.error('Salve as alterações desta guia antes de atualizar os preços.');
        return;
      }
      this.reprice.loading = true;
      try {
        const preview = await repricePreview(this.account.id);
        this.divergence = preview;
        if (preview.changed === 0) {
          this.$toast.success('Nada a atualizar: a guia já está com a regra vigente.');
          return;
        }
        this.reprice.preview = preview;
        this.reprice.show = true;
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.reprice.loading = false;
      }
    },
    async applyReprice() {
      this.reprice.applying = true;
      try {
        const result = await repriceApply(this.account.id);
        this.account = result;
        this.form = this.formFromAccount(this.account);
        this.applyFillRules(this.form);
        this.snapshot = JSON.stringify(this.form);
        this.divergence = null;
        this.reprice.show = false;
        this.reprice.preview = null;
        this.$toast.success('Preços e repasse atualizados.');
        this.$emit('saved', result);
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.reprice.applying = false;
      }
    },
    async loadProfessionals() {
      const items = await this.apiFetchProfessionals();
      const list = [];
      items.forEach((item) => {
        const professional = item.professional || {};
        const councils = professional.councils || [];
        const council = councils.length > 0
          ? councils[0]
          : { name: '', state: '', record: '' };
        const specialties = professional.specialties && professional.specialties.length > 0
          ? professional.specialties
          : [null];
        specialties.forEach((specialty) => {
          list.push({
            id: item.id,
            name: item.name,
            council,
            specialty: specialty ? { code: specialty.code, name: specialty.name } : null,
            specialties: specialties.length,
          });
        });
      });
      this.professionals = list;
    },
    async loadPlanSettings(insuranceId, planId) {
      this.plan = await loadPlan(insuranceId, planId);
      this.planSettings = this.plan && this.plan.settings ? this.plan.settings : {};
    },
    async loadAccount() {
      this.account = await get(this.accountId);
      await this.loadPlanSettings(this.account.insurance.id, this.account.insurance.plan.id);
      this.form = this.formFromAccount(this.account);
      this.appendPreselected();
      if (!this.isReadonly) this.applyFillRules(this.form);
    },
    appendPreselected() {
      if (this.isReadonly || this.isConsultation || !this.appointment
        || this.preselectedExpenseIds.length === 0) return;
      const current = this.form.items.map(item => String(item.appointmentExpenseId));
      const wanted = this.preselectedExpenseIds.map(String);
      const added = (this.appointment.expenses || [])
        .filter(expense => expense.id && wanted.includes(String(expense.id))
          && !current.includes(String(expense.id)) && this.isCandidate(expense))
        .map(expense => this.itemFromExpense(expense, this.appointment));
      this.form.items.push(...added);
      this.limitConsultations(this.form.items);
    },
    async buildNew() {
      if (!this.appointment || !this.insurance) {
        throw new Error('Informe o agendamento e o convênio da guia.');
      }
      await this.loadPlanSettings(this.insurance.id, this.insurance.planId);
      this.form = this.formFromAppointment(this.appointment);
    },
    professionalByIds(professionalId, specialtyCode) {
      return this.professionals.find(item => item.id === professionalId
        && (!specialtyCode || !item.specialty || item.specialty.code === specialtyCode))
        || this.professionals.find(item => item.id === professionalId)
        || null;
    },
    formFromAccount(account) {
      const form = blankForm();
      const request = account.request || {};
      const requester = request.professional || {};
      const insured = account.insurance.insured || {};
      const auth = account.authorization || {};

      form.startDate = toDate(account.startDate);
      form.startTime = toTime(account.startDate);
      form.referral = {
        provider: orEmpty(account.referral.provider),
        insurance: orEmpty(account.referral.insurance),
        main: orEmpty(account.referral.main),
      };
      form.insured = {
        record: orEmpty(insured.record),
        name: orEmpty(insured.name),
        validity: toDate(insured.validity),
      };
      form.patient = {
        cns: orEmpty(account.patient.cns),
        socialName: orEmpty(account.patient.socialName),
        isNewborn: !!account.patient.isNewborn,
      };
      form.request = {
        professionalId: idOf(requester),
        professional: {
          name: orEmpty(requester.name),
          council: {
            name: orEmpty(requester.council ? requester.council.name : ''),
            state: orEmpty(requester.council ? requester.council.state : ''),
            record: orEmpty(requester.council ? requester.council.record : ''),
          },
          specialty: {
            code: orEmpty(requester.specialty ? requester.specialty.code : ''),
            name: orEmpty(requester.specialty ? requester.specialty.name : ''),
          },
        },
        date: toDate(request.date),
      };
      form.authorization = {
        date: toDate(auth.date),
        password: orEmpty(auth.password),
        validity: toDate(auth.validity),
      };
      Object.keys(form.service).forEach((key) => {
        form.service[key] = orEmpty(account.service[key]);
      });
      form.courtesy = !!account.courtesy;
      form.professionalId = account.professional.id;
      form.specialtyCode = account.professional.specialty
        ? account.professional.specialty.code
        : null;
      form.items = account.items.map(item => this.itemFromAccount(item));
      return form;
    },
    itemFromAccount(item) {
      const built = blankItem();
      const executantId = item.transfer.professionalId || item.professional.id;
      const specialtyCode = item.professional.specialty ? item.professional.specialty.code : null;
      const executant = this.professionalByIds(executantId, specialtyCode);
      Object.assign(built, {
        key: item.id,
        id: item.id,
        selected: true,
        appointmentExpenseId: item.appointmentExpenseId,
        splitIndex: item.splitIndex || 0,
        expenseId: item.expense.id,
        expense: {
          id: item.expense.id,
          code: item.expense.code,
          name: item.expense.name,
          type: item.expense.type,
          tableCode: item.expense.tableCode,
        },
        kind: item.kind || kindOf(item.expense.type),
        professionalId: executantId,
        specialtyCode: executant && executant.specialty ? executant.specialty.code : specialtyCode,
        professionalName: executant ? executant.name : item.professional.name,
        accreditedName: item.professional.id !== executantId ? item.professional.name : null,
        quantity: item.quantity,
        factor: item.factor || 1,
        factorEdited: true,
        return: !!item.return,
        way: orEmpty(item.way),
        technique: orEmpty(item.technique),
        rules: item.rules || null,
        degreeParticipation: orEmpty(item.professional.degreeParticipation),
        executionDate: toDate(item.executionDate),
        startTime: orEmpty(item.startTime),
        endTime: orEmpty(item.endTime),
        sequencialItem: item.sequencialItem,
        unit: { insurance: item.unit.insurance || 0, particular: item.unit.particular || 0 },
        value: {
          insurance: item.value.insurance || 0,
          particular: item.value.particular || 0,
          total: item.value.total || 0,
        },
        priced: !!item.price.priced,
        source: item.price.source,
      });
      return built;
    },
    formFromAppointment(appointment) {
      const form = blankForm();
      const professional = appointment.professional || {};
      const patient = appointment.patient || {};
      const appointmentInsurance = appointment.insurance || {};
      const sameInsurance = idOf(appointmentInsurance) === this.insurance.id;
      const specialty = professional.specialty && typeof professional.specialty === 'object'
        ? professional.specialty.code
        : professional.specialty;

      form.startDate = toDate(appointment.startDate);
      form.startTime = toTime(appointment.startDate);
      form.professionalId = idOf(professional);
      form.specialtyCode = specialty || null;
      form.insured = {
        record: sameInsurance ? orEmpty(appointmentInsurance.record) : '',
        name: patient.name || '',
        validity: sameInsurance ? toDate(appointmentInsurance.validity) : '',
      };
      form.patient = {
        cns: orEmpty(patient.cns),
        socialName: orEmpty(patient.socialName),
        isNewborn: false,
      };
      form.service.accidentIndication = 'not_accident';
      form.service.serviceRegime = 'outpatient';
      if (this.type === 'consultation') {
        form.service.type = 'consultation';
      }
      if (this.type === 'sadt') {
        form.service.character = 'elective';
      }

      const preselected = this.preselectedExpenseIds.map(String);
      const candidates = (appointment.expenses || []).filter(expense => this.isCandidate(expense)
        && (preselected.length === 0 || preselected.includes(String(expense.id))));
      form.items = candidates.map((expense) => {
        const item = this.itemFromExpense(expense, appointment);
        item.selected = true;
        return item;
      });

      if (this.type === 'consultation') {
        const chosen = form.items.find(item => item.selected) || form.items[0] || null;
        form.items.forEach((item) => {
          item.selected = item === chosen;
        });
        form.service.consultationType = chosen && chosen.return ? 'return' : 'first';
      }
      this.limitConsultations(form.items);
      if (this.type === 'sadt' && form.items.length > 0
        && form.items.every(item => item.expense.type === 'consultation')) {
        form.service.type = 'consultation';
      }

      this.applyFillRules(form);
      return form;
    },
    limitConsultations(items) {
      if (this.currentType !== 'sadt') return;
      let kept = false;
      items.forEach((item) => {
        if (item.expense.type !== 'consultation') return;
        if (kept) item.selected = false;
        if (item.selected) kept = true;
      });
    },
    isCandidate(expense) {
      const billing = expense.billing || {};
      if (idOf(billing.accountId)) return false;
      if (this.isConsultation && !(expense.expense && expense.expense.type === 'consultation')) {
        return false;
      }
      if (expense.accountId && !billing.accountId && expense.paid) return false;
      const insurance = expense.insurance || {};
      const plan = insurance.plan || {};
      const planId = idOf(plan);
      if (planId && planId !== this.planId) return false;
      if (!planId && this.appointment && this.appointment.insurance
        && idOf(this.appointment.insurance.plan) !== this.planId) return false;
      return true;
    },
    itemFromExpense(expense, appointment) {
      const built = blankItem();
      const expenseProfessional = expense.professional || {};
      const professionalId = idOf(expenseProfessional) || idOf(appointment.professional);
      const expenseSpecialty = expenseProfessional.specialty && typeof expenseProfessional.specialty === 'object'
        ? expenseProfessional.specialty.code
        : expenseProfessional.specialty;
      const executant = this.professionalByIds(
        professionalId,
        expenseSpecialty || this.form.specialtyCode,
      );
      Object.assign(built, {
        key: String(expense.id),
        id: null,
        appointmentExpenseId: String(expense.id),
        expenseId: idOf(expense.expense),
        expense: {
          id: idOf(expense.expense),
          code: expense.expense.code,
          name: expense.expense.name,
          type: expense.expense.type,
          tableCode: null,
        },
        kind: kindOf(expense.expense.type),
        degreeParticipation: this.currentType === 'sadt' && kindOf(expense.expense.type) !== 'other_expense'
          ? 'clinical'
          : '',
        professionalId,
        specialtyCode: executant && executant.specialty
          ? executant.specialty.code
          : (expenseSpecialty || null),
        professionalName: executant ? executant.name : expenseProfessional.name || null,
        quantity: this.currentType === 'sadt' && expense.expense.type === 'consultation'
          ? 1
          : expense.quantity || 1,
        factor: expense.factor || 1,
        return: !!expense.return,
        executionDate: toDate(appointment.startDate),
        value: {
          insurance: Number(expense.totalInsurance) || 0,
          particular: Number(expense.totalParticular) || 0,
          total: (Number(expense.totalInsurance) || 0) + (Number(expense.totalParticular) || 0),
        },
        unit: {
          insurance: Number(expense.unitInsurance) || 0,
          particular: Number(expense.unitParticular) || 0,
        },
      });
      return built;
    },
    applyFillRules(form) {
      if (this.recordSettings.fillValidityWithServiceDate && !form.insured.validity) {
        form.insured.validity = form.startDate;
      }
      if (this.currentType === 'sadt') {
        if (this.sadtSettings.fillAuthorizationDateWithServiceDate && !form.authorization.date) {
          form.authorization.date = form.startDate;
        }
        if (this.sadtSettings.fillRequestDataWithServiceDate && !form.request.date) {
          form.request.date = form.startDate;
        }
      }
      this.fillAuthorizationValidity(form);
    },
    fillAuthorizationValidity(form = this.form) {
      const settings = this.authorizationSettings.fillValidityDate || {};
      if (!settings.enabled || !form.authorization.date || form.authorization.validity) return;
      form.authorization.validity = moment(form.authorization.date)
        .add(Number(settings.days) || 0, 'days')
        .format('YYYY-MM-DD');
    },
    fillsInsuranceReferralWithPassword() {
      return this.currentType === 'sadt'
        && !!this.authorizationSettings.fillInsuranceReferralWithPassword;
    },
    syncProviderReferral() {
      if (this.providerMode === 'password') {
        this.form.referral.provider = this.form.authorization.password;
      }
      if (this.providerMode === 'insurance_referral') {
        this.form.referral.provider = this.form.referral.insurance;
      }
    },
    passwordChanged() {
      if (this.fillsInsuranceReferralWithPassword()) {
        this.form.referral.insurance = this.form.authorization.password;
      }
      this.syncProviderReferral();
    },
    insuranceReferralChanged() {
      if (this.fillsInsuranceReferralWithPassword()) {
        this.form.authorization.password = this.form.referral.insurance;
      }
      this.syncProviderReferral();
    },
    selectConsultation(item) {
      this.form.items.forEach((found) => {
        found.selected = found === item;
      });
      this.form.service.consultationType = item.return ? 'return' : 'first';
      if (!item.priced && !item.pricingError) this.repriceItems([item]);
    },
    consultationTypeChanged() {
      const isReturn = this.form.service.consultationType === 'return';
      const changed = [];
      this.form.items.forEach((item) => {
        if (item.expense.type === 'consultation' && item.return !== isReturn) {
          item.return = isReturn;
          changed.push(item);
        }
      });
      if (changed.length > 0) this.repriceItems(changed);
    },
    setCourtesy(value) {
      this.form.courtesy = value;
      this.repriceItems(this.form.items);
    },
    factorEdited(item) {
      item.factorEdited = true;
      this.repriceItems([item]);
    },
    pricingRequest(item) {
      return {
        key: item.key,
        expenseId: item.expenseId,
        appointmentExpenseId: item.appointmentExpenseId || undefined,
        insuranceId: this.insuranceId,
        planId: this.planId,
        professionalId: item.professionalId || undefined,
        specialtyCode: item.specialtyCode || undefined,
        quantity: Number(item.quantity) || 1,
        factor: Number(item.factor) || 1,
        autoFactor: !this.isConsultation && item.selected && !item.factorEdited,
        return: !!item.return,
        courtesy: !!this.form.courtesy,
      };
    },
    pricingBatches(targets) {
      if (this.isConsultation) return [targets];
      const selected = targets.filter(item => item.selected);
      const unselected = targets.filter(item => !item.selected);
      const keys = selected.map(item => item.key);
      const others = selected.length > 0
        ? this.selectedItems.filter(item => item.expenseId && !keys.includes(item.key))
        : [];
      return [[...selected, ...others], unselected].filter(batch => batch.length > 0);
    },
    async repriceItems(items) {
      const targets = (items || []).filter(item => item && item.expenseId && this.planId);
      if (targets.length === 0) return;
      const batches = this.pricingBatches(targets);
      const keys = batches.flat().map(item => item.key);
      this.pricingKeys = [...new Set([...this.pricingKeys, ...keys])];
      try {
        const responses = await Promise.all(batches
          .map(batch => resolvePrices(
            batch.map(item => this.pricingRequest(item)),
            this.appointmentId,
          )));
        responses.flat().forEach((price) => {
          const item = this.form.items.find(found => found.key === price.key);
          if (!item) return;
          const totals = price.totals || {};
          if (!item.factorEdited && Number(price.factor) > 0) {
            item.factor = Number(price.factor);
          }
          item.reduction = price.reduction || null;
          item.unit = {
            insurance: price.unit ? price.unit.insurance || 0 : 0,
            particular: price.unit ? price.unit.particular || 0 : 0,
          };
          item.value = {
            insurance: totals.insurance || 0,
            particular: totals.particular || 0,
            total: totals.total || 0,
          };
          item.priced = !!price.priced;
          item.source = price.source || 'none';
          item.pricingError = false;
          item.rules = price.rules || null;
          if (price.expense && price.expense.tableCode) {
            item.expense.tableCode = price.expense.tableCode;
          }
          const accreditation = price.transfer ? price.transfer.accreditation : null;
          item.accreditedName = accreditation && accreditation.name ? accreditation.name : null;
        });
        if (this.splitReducedItems()) await this.repriceItems(this.selectedItems);
      } catch (e) {
        batches.flat().forEach((item) => {
          item.pricingError = true;
        });
        this.$toast.error(e);
      } finally {
        this.pricingKeys = this.pricingKeys.filter(key => !keys.includes(key));
      }
    },
    siblingsOf(item) {
      return this.form.items
        .filter(found => found.appointmentExpenseId === item.appointmentExpenseId);
    },
    splitReducedItems() {
      if (this.isConsultation || this.isReadonly) return false;
      const targets = this.form.items.filter(item => item.selected && item.reduction
        && !item.factorEdited && Number(item.quantity) > 1);
      targets.forEach((item) => {
        const quantity = Number(item.quantity);
        const lastIndex = Math.max(...this.siblingsOf(item).map(found => found.splitIndex || 0));
        item.quantity = 1;
        const clones = Array.from({ length: quantity - 1 }, (_, offset) => {
          const splitIndex = lastIndex + offset + 1;
          return {
            ...JSON.parse(JSON.stringify(item)),
            id: null,
            key: `${item.appointmentExpenseId}:${splitIndex}`,
            splitIndex,
            quantity: 1,
          };
        });
        const position = this.form.items.indexOf(item);
        this.form.items.splice(position + 1, 0, ...clones);
      });
      return targets.length > 0;
    },
    toggleItem(item) {
      this.siblingsOf(item).forEach((found) => {
        found.selected = item.selected;
      });
      this.repriceItems(this.selectedItems);
    },
    async removeItem(item) {
      if (!item.id) {
        const keys = this.siblingsOf(item).filter(found => !found.id).map(found => found.key);
        this.form.items = this.form.items.filter(found => !keys.includes(found.key));
        if (!this.isConsultation) this.repriceItems(this.selectedItems);
        return;
      }
      if (this.form.items.length === 1) {
        this.$toast.error('A guia precisa de ao menos um item. Exclua a guia se for o caso.');
        return;
      }
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente remover '${item.expense.name}' desta guia? `
          + 'O serviço volta a ficar pendente no agendamento.</div></div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.confirmRemoveItem(item);
            },
          },
        ],
      });
    },
    async confirmRemoveItem(item) {
      this.removingKey = item.key;
      const siblings = this.siblingsOf(item);
      const keys = siblings.map(found => found.key);
      try {
        const result = await siblings.filter(sibling => sibling.id)
          .reduce(async (previous, found) => {
            const last = await previous;
            if (last && last.deleted) return last;
            return removeItem(this.account.id, found.id);
          }, Promise.resolve(null));
        if (result.deleted) {
          this.$toast.success('Guia excluída.');
          this.$emit('deleted', { id: this.account.id, appointments: result.appointments || [] });
          this.$emit('close', true);
          return;
        }
        this.account = result;
        this.form.items = this.form.items.filter(found => !keys.includes(found.key));
        this.snapshot = JSON.stringify(this.form);
        this.$toast.success('Item removido da guia.');
        this.$emit('saved', result);
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.removingKey = null;
      }
    },
    async openAddItems() {
      this.addModal.selected = [];
      this.addModal.items = [];
      this.addModal.show = true;
      this.addModal.loading = true;
      try {
        if (!this.appointmentData) {
          this.appointmentData = this.appointment && this.appointment.expenses
            ? this.appointment
            : await loadAppointment(this.appointmentId);
        }
        const current = this.form.items.map(item => String(item.appointmentExpenseId));
        this.addModal.items = (this.appointmentData.expenses || [])
          .filter(expense => expense.id && !current.includes(String(expense.id)))
          .filter(expense => this.isCandidate(expense));
      } catch (e) {
        this.$toast.error(e);
        this.addModal.show = false;
      } finally {
        this.addModal.loading = false;
      }
    },
    toggleAddItem(expense) {
      const id = String(expense.id);
      if (this.addModal.selected.includes(id)) {
        this.addModal.selected = this.addModal.selected.filter(found => found !== id);
        return;
      }
      if (this.currentType === 'sadt' && expense.expense.type === 'consultation'
        && this.hasConsultationForAdd()) {
        this.$toast.error('A guia SP/SADT aceita no máximo uma consulta.');
        return;
      }
      this.addModal.selected.push(id);
    },
    hasConsultationForAdd() {
      return this.selectedConsultationCount > 0 || this.addModal.items
        .some(expense => this.addModal.selected.includes(String(expense.id))
          && expense.expense.type === 'consultation');
    },
    confirmAddItems() {
      const added = this.addModal.items
        .filter(expense => this.addModal.selected.includes(String(expense.id)))
        .map(expense => this.itemFromExpense(expense, this.appointmentData));
      this.form.items.push(...added);
      this.addModal.show = false;
      this.repriceItems(added);
    },
    focusField(field) {
      if (!field) return;
      if (field === 'items') {
        this.$v.selectedCount.$touch();
        this.$v.selectedConsultationCount.$touch();
        if (this.$refs.items) this.$refs.items.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const node = field.split('.').reduce((current, key) => (current ? current[key] : null), this.$v.form);
      if (node && typeof node.$touch === 'function') node.$touch();
      const el = document.getElementById(`guide-field-${field.replace(/\./g, '-')}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function') el.focus();
      }
    },
    payload() {
      const { form } = this;
      const startDate = moment(`${form.startDate} ${form.startTime || '00:00'}`, 'YYYY-MM-DD HH:mm');
      const type = this.currentType;
      const provider = form.referral.provider
        || (this.providerMode === 'automatic' && this.isNew ? AUTO_REFERRAL : '');
      const requester = form.request.professional;
      const hasRequester = !!(form.request.professionalId || requester.name);
      const isConsultation = type === 'consultation';

      const service = {};
      Object.keys(form.service).forEach((key) => {
        service[key] = nullable(form.service[key]);
      });
      if (type === 'consultation') service.type = 'consultation';

      return {
        appointmentId: this.appointmentId,
        type,
        insurance: { id: this.insuranceId, planId: this.planId },
        patientId: this.account ? this.account.patient.id : idOf(this.appointment.patient),
        professionalId: form.professionalId,
        specialtyCode: nullable(form.specialtyCode),
        startDate: startDate.format('YYYY-MM-DDTHH:mm:ss'),
        referral: {
          provider: nullable(provider),
          insurance: nullable(form.referral.insurance),
          main: nullable(form.referral.main),
        },
        authorization: {
          date: isConsultation ? null : nullable(form.authorization.date),
          password: nullable(form.authorization.password),
          validity: isConsultation ? null : nullable(form.authorization.validity),
        },
        request: type === 'sadt' && (hasRequester || form.request.date)
          ? {
            professionalId: nullable(form.request.professionalId),
            specialtyCode: nullable(requester.specialty.code),
            professional: {
              name: nullable(requester.name),
              council: {
                name: nullable(requester.council.name),
                state: nullable(requester.council.state),
                record: nullable(requester.council.record),
              },
              specialty: {
                code: nullable(requester.specialty.code),
                name: nullable(requester.specialty.name),
              },
            },
            date: nullable(form.request.date),
          }
          : null,
        service,
        courtesy: !!form.courtesy,
        insured: {
          record: nullable(form.insured.record),
          name: nullable(form.insured.name),
          validity: nullable(form.insured.validity),
        },
        patient: {
          isNewborn: !!form.patient.isNewborn,
          cns: nullable(form.patient.cns),
          socialName: nullable(form.patient.socialName),
        },
        items: this.selectedItems.map(item => ({
          appointmentExpenseId: item.appointmentExpenseId,
          splitIndex: item.splitIndex || 0,
          professionalId: nullable(item.professionalId),
          specialtyCode: nullable(item.specialtyCode),
          quantity: isConsultation ? 1 : Number(item.quantity) || 1,
          factor: isConsultation ? 1 : Number(item.factor) || 1,
          return: !!item.return,
          way: isConsultation ? null : nullable(item.way),
          technique: isConsultation ? null : nullable(item.technique),
          degreeParticipation: isConsultation ? null : nullable(item.degreeParticipation),
          executionDate: nullable(item.executionDate),
          startTime: nullable(item.startTime),
          endTime: nullable(item.endTime),
        })),
      };
    },
    async save() {
      if (!this.canSave) return;
      this.$v.$touch();
      if (this.$v.$error) {
        this.$toast.error('Revise os campos destacados.');
        const firstError = this.$el.querySelector('.has-error');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (this.pending.length > 0) {
        this.$toast.error(`Complete a guia antes de salvar: ${this.pending
          .map(issue => issue.message).join('; ')}.`);
        return;
      }
      this.saving = true;
      try {
        const payload = this.payload();
        const result = this.isNew
          ? await create(payload)
          : await update(this.account.id, payload);
        this.$toast.success(this.isNew
          ? `${getLongName(this.currentType)} nº ${result.referral.provider} lançada.`
          : 'Guia atualizada.');
        this.$emit('saved', result);
        this.$emit('close', true);
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.saving = false;
      }
    },
    print(request = false) {
      if (!this.account) return;
      this.printing = request ? 'request' : 'guide';
      const route = request ? printRequestRoute(this.account.id) : printRoute(this.account.id);
      this.$file.print(route)
        .catch(() => {})
        .finally(() => {
          this.printing = null;
        });
    },
    close(saved = false) {
      if (this.saving) return;
      if (!saved && !this.isReadonly && this.ready && this.dirty) {
        this.$dialog.show('', {
          html: '<div class="text-center">'
            + '<h5 class="text-center">Atenção!</h5>'
            + '<div>Há alterações não salvas nesta guia. Sair mesmo assim?</div></div>',
          buttons: [
            { label: 'Não', classes: '' },
            {
              label: 'Sim',
              classes: 'btn-primary btn-error ml-2',
              click: (dialogClose) => {
                dialogClose();
                this.$emit('close', false);
              },
            },
          ],
        });
        return;
      }
      this.$emit('close', saved);
    },
    onKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        this.save();
      }
    },
  },
};
</script>

<style lang="scss">
@import "../guide.scss";


#modal-billing-guide {
  .modal-body {
    background-color: #F3F4F7;
    padding-bottom: $layout-spacing-lg;
    padding-top: $layout-spacing-lg;
  }
  .gd-loading {
    margin: $layout-spacing-xl 0;
  }
  .gd-switch-column {
    .form-switch {
      margin: .1rem 0 0;
    }
  }
  .gd-items {
    margin-bottom: $layout-spacing;
  }
}
</style>
